// Die Eingabefelder der Schritte, aufgeklappt unter der Schrittzeile (eine Spalte, kein Seitenfenster).
import { useEffect, useState, type ReactNode } from "react";
import type { Bedingung, Schritt } from "../api/workflows";
import { Auswahl, type Option } from "../components/Auswahl";
import { FeldEingabe } from "../components/FeldEingabe";
import { Icon } from "../components/Icon";
import { useMeta } from "../daten/meta";
import { komm } from "../kommunikation/api";
import type { Vorlage } from "../kommunikation/typen";
import type { FeldDef } from "../werkzeuge/felder";
import { Bedingungen } from "./Bedingungen";

type Config = Record<string, unknown>;

interface Props {
  schritt: Schritt;
  onChange: (config: Config) => void;
}

const HINWEIS_PLATZHALTER = "Platzhalter wie {{ lead.name }} oder {{ contact.first_name }} werden beim Ausführen eingesetzt.";

function Feld({ label, children, hinweis, breit }: { label: string; children: ReactNode; hinweis?: string; breit?: boolean }) {
  return (
    <div className={`feld wf-feld${breit ? " wf-feld--breit" : ""}`}>
      <span className="feld__label">{label}</span>
      {children}
      {hinweis ? <span className="leise wf-feld__hinweis">{hinweis}</span> : null}
    </div>
  );
}

function Haken({ an, onChange, children }: { an: boolean; onChange: (an: boolean) => void; children: ReactNode }) {
  return (
    <label className="wf-haken">
      <input type="checkbox" checked={an} onChange={(e) => onChange(e.target.checked)} />
      <span>{children}</span>
    </label>
  );
}

function NutzerAuswahl({ wert, onChange, platzhalter = "Nutzer wählen …", leer = false }: { wert: string | null; onChange: (id: string | null) => void; platzhalter?: string; leer?: boolean }) {
  const { nutzerListe } = useMeta();
  const optionen: Option[] = nutzerListe.filter((u) => u.is_active || u.id === wert || u.close_user_id === wert).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name, unter: u.is_active ? null : "inaktiv" }));
  return <Auswahl optionen={optionen} wert={wert} platzhalter={platzhalter} leerErlaubt={leer} onWahl={(w) => onChange((w as string | null) ?? null)} />;
}

function useVorlagen(art: "email" | "sms"): Vorlage[] {
  const [liste, setListe] = useState<Vorlage[]>([]);
  useEffect(() => {
    let weg = false;
    komm
      .vorlagen(art)
      .then((r) => {
        if (!weg) setListe(r.data);
      })
      .catch(() => undefined);
    return () => {
      weg = true;
    };
  }, [art]);
  return liste;
}

function VorlageWahl({ art, wert, onChange }: { art: "email" | "sms"; wert: string | null; onChange: (id: string | null) => void }) {
  const vorlagen = useVorlagen(art);
  return (
    <Auswahl
      optionen={vorlagen.map((v) => ({ wert: v.id, label: v.name }))}
      wert={wert}
      leerErlaubt
      platzhalter={art === "email" ? "Keine Vorlage, eigener Text" : "Keine Vorlage, eigener Text"}
      onWahl={(w) => onChange((w as string | null) ?? null)}
    />
  );
}

// --- Aufgabe und Anrufaufgabe ------------------------------------------------------------------------------------

function AufgabeForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const set = (teil: Config) => onChange({ ...c, ...teil });
  const anruf = schritt.art === "anrufaufgabe";
  return (
    <div className="wf-form">
      <Feld label={anruf ? "Text (leer = „Anrufen: Name“)" : "Text"} hinweis={HINWEIS_PLATZHALTER} breit>
        <input className="eingabe" value={String(c.text ?? "")} placeholder={anruf ? "Anrufen" : "Rückruf bei {{ lead.name }}"} onChange={(e) => set({ text: e.target.value })} />
      </Feld>
      <div className="wf-form__zeile">
        <Feld label="Fällig in">
          <span className="wf-inline">
            <input className="eingabe eingabe--schmal" type="number" min={0} max={365} value={Number(c.tage ?? 0)} onChange={(e) => set({ tage: Math.max(0, Number(e.target.value) || 0) })} aria-label="Tage" />
            <span className="leise">Tagen, um</span>
            <input className="eingabe eingabe--uhrzeit" type="time" value={String(c.uhrzeit ?? "")} onChange={(e) => set({ uhrzeit: e.target.value || null })} aria-label="Uhrzeit (leer = ganztägig)" />
          </span>
        </Feld>
        <Feld label="Priorität">
          <Auswahl
            optionen={[
              { wert: "hoch", label: "Hoch" },
              { wert: "normal", label: "Normal" },
              { wert: "niedrig", label: "Niedrig" },
            ]}
            wert={(c.prioritaet as string | null) ?? "normal"}
            onWahl={(w) => set({ prioritaet: w === "normal" ? null : w })}
          />
        </Feld>
      </div>
      <div className="wf-form__zeile">
        <Feld label="Zuständig">
          <Auswahl
            optionen={[
              { wert: "ausfuehrender", label: "Der Workflow-Nutzer (siehe Einstellungen)" },
              { wert: "bearbeiter", label: "Bearbeiter des Leads" },
              { wert: "nutzer", label: "Bestimmter Nutzer" },
            ]}
            wert={(c.zustaendig as string) ?? "ausfuehrender"}
            onWahl={(w) => set({ zustaendig: w })}
          />
        </Feld>
        {c.zustaendig === "nutzer" ? (
          <Feld label="Nutzer">
            <NutzerAuswahl wert={(c.nutzer_id as string | null) ?? null} onChange={(id) => set({ nutzer_id: id })} />
          </Feld>
        ) : null}
      </div>
      <Haken an={!!c.pflicht} onChange={(an) => set({ pflicht: an })}>
        Pflichtaufgabe: der Lauf wartet, bis sie erledigt ist
      </Haken>
    </div>
  );
}

// --- E-Mail und SMS (nur Entwurf) --------------------------------------------------------------------------------

function EmailForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const set = (teil: Config) => onChange({ ...c, ...teil });
  const vorlage = (c.vorlage_id as string | null) ?? null;
  return (
    <div className="wf-form">
      <div className="hinweis wf-form__hinweis">
        <Icon name="info" groesse={15} />
        <span>Es entsteht nur ein Entwurf zur Prüfung. Gesendet wird erst, wenn du ihn im E-Mail-Fenster abschickst.</span>
      </div>
      <Feld label="Vorlage">
        <VorlageWahl art="email" wert={vorlage} onChange={(id) => set({ vorlage_id: id })} />
      </Feld>
      {!vorlage ? (
        <>
          <Feld label="Betreff" breit>
            <input className="eingabe" value={String(c.betreff ?? "")} placeholder="Kurze Nachfrage, {{ contact.first_name }}" onChange={(e) => set({ betreff: e.target.value })} />
          </Feld>
          <Feld label="Text" hinweis={HINWEIS_PLATZHALTER} breit>
            <textarea className="eingabe eingabe--text" rows={6} value={String(c.text ?? "")} onChange={(e) => set({ text: e.target.value })} />
          </Feld>
        </>
      ) : null}
      <div className="wf-form__zeile">
        <Feld label="Empfänger">
          <Auswahl
            optionen={[
              { wert: "erster", label: "Erster Kontakt mit E-Mail" },
              { wert: "alle", label: "Alle Kontakte mit E-Mail" },
            ]}
            wert={(c.empfaenger as string) ?? "erster"}
            onWahl={(w) => set({ empfaenger: w })}
          />
        </Feld>
        <Feld label="Absender">
          <NutzerAuswahl wert={(c.absender_user_id as string | null) ?? null} leer platzhalter="Der Workflow-Nutzer" onChange={(id) => set({ absender_user_id: id })} />
        </Feld>
      </div>
      <Haken an={c.aufgabe !== false} onChange={(an) => set({ aufgabe: an })}>
        Aufgabe „Entwurf prüfen und senden“ anlegen (heute fällig)
      </Haken>
    </div>
  );
}

function SmsForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const set = (teil: Config) => onChange({ ...c, ...teil });
  const vorlage = (c.vorlage_id as string | null) ?? null;
  return (
    <div className="wf-form">
      <div className="hinweis wf-form__hinweis">
        <Icon name="info" groesse={15} />
        <span>Es entsteht eine Aufgabe „SMS prüfen und senden“ mit dem vorbereiteten Text. Gesendet wird nichts von allein.</span>
      </div>
      <Feld label="Vorlage">
        <VorlageWahl art="sms" wert={vorlage} onChange={(id) => set({ vorlage_id: id })} />
      </Feld>
      {!vorlage ? (
        <Feld label="Text" hinweis={HINWEIS_PLATZHALTER} breit>
          <textarea className="eingabe eingabe--text" rows={4} value={String(c.text ?? "")} onChange={(e) => set({ text: e.target.value })} />
        </Feld>
      ) : null}
      <div className="wf-form__zeile">
        <Feld label="Nummer">
          <Auswahl
            optionen={[
              { wert: "mobil", label: "Mobilnummer bevorzugt" },
              { wert: "erste", label: "Erste Nummer" },
            ]}
            wert={(c.telefon as string) ?? "mobil"}
            onWahl={(w) => set({ telefon: w })}
          />
        </Feld>
        <Feld label="Prüfen und senden soll">
          <NutzerAuswahl wert={(c.nutzer_id as string | null) ?? null} leer platzhalter="Der Workflow-Nutzer" onChange={(id) => set({ nutzer_id: id })} />
        </Feld>
      </div>
    </div>
  );
}

// --- Zuweisen, Lead ändern -----------------------------------------------------------------------------------------

function ZuweisenForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const set = (teil: Config) => onChange({ ...c, ...teil });
  const modus = (c.modus as string) ?? "nutzer";
  const { nutzerListe } = useMeta();
  return (
    <div className="wf-form">
      <Feld label="Bearbeiter">
        <Auswahl
          optionen={[
            { wert: "nutzer", label: "Bestimmter Nutzer" },
            { wert: "zufall", label: "Zufällig aus mehreren Nutzern" },
            { wert: "ausfuehrender", label: "Der Workflow-Nutzer" },
            { wert: "leeren", label: "Bearbeiter entfernen" },
          ]}
          wert={modus}
          onWahl={(w) => set({ modus: w })}
        />
      </Feld>
      {modus === "nutzer" ? (
        <Feld label="Nutzer">
          <NutzerAuswahl wert={(c.nutzer_id as string | null) ?? null} onChange={(id) => set({ nutzer_id: id })} />
        </Feld>
      ) : null}
      {modus === "zufall" ? (
        <Feld label="Nutzer zur Auswahl">
          <Auswahl
            optionen={nutzerListe.filter((u) => u.is_active).map((u) => ({ wert: u.close_user_id ?? u.id, label: u.name }))}
            wert={(c.nutzer_ids as string[]) ?? []}
            mehrfach
            platzhalter="Nutzer wählen …"
            onWahl={(w) => set({ nutzer_ids: w })}
          />
        </Feld>
      ) : null}
      {modus !== "leeren" ? (
        <Haken an={c.ueberschreiben !== false} onChange={(an) => set({ ueberschreiben: an })}>
          Einen vorhandenen Bearbeiter überschreiben
        </Haken>
      ) : null}
    </div>
  );
}

function LeadAendernForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const { meta } = useMeta();
  const set = (teil: Config) => onChange({ ...c, ...teil });
  const felder = (c.felder as Record<string, unknown>) ?? {};
  const defs: FeldDef[] = (meta?.custom_fields.lead ?? []).filter((f) => f.type !== "hidden" && f.type !== "custom_object" && f.type !== "contact") as FeldDef[];
  const frei = defs.filter((d) => !(d.id in felder));
  return (
    <div className="wf-form">
      <Feld label="Status setzen">
        <Auswahl
          optionen={(meta?.lead_statuses ?? []).map((s) => ({ wert: s.id, label: s.label, farbe: s.color }))}
          wert={(c.status_id as string | null) ?? null}
          leerErlaubt
          platzhalter="Status nicht ändern"
          onWahl={(w) => set({ status_id: (w as string | null) ?? null })}
        />
      </Feld>
      {Object.entries(felder).map(([id, wert]) => {
        const def = defs.find((d) => d.id === id);
        return (
          <div key={id} className="wf-feldzeile">
            <span className="wf-feldzeile__name" title={def?.name ?? id}>
              {def?.name ?? id}
            </span>
            <div className="wf-feldzeile__wert">
              {def ? <FeldEingabe def={def} wert={wert as never} onChange={(w) => set({ felder: { ...felder, [id]: w } })} /> : <span className="leise">Feld gibt es nicht mehr</span>}
            </div>
            <button
              type="button"
              className="symbolknopf"
              title="Feld entfernen"
              aria-label="Feld entfernen"
              onClick={() => {
                const rest = { ...felder };
                delete rest[id];
                set({ felder: rest });
              }}
            >
              <Icon name="schliessen" groesse={14} />
            </button>
          </div>
        );
      })}
      <div>
        <Auswahl
          optionen={frei.map((f) => ({ wert: f.id, label: f.name }))}
          wert={null}
          platzhalter="+ Feld setzen"
          knopfKlasse="knopf knopf--klein"
          breite={260}
          onWahl={(w) => w && set({ felder: { ...felder, [w as string]: null } })}
        />
      </div>
      <Haken an={c.ueberschreiben !== false} onChange={(an) => set({ ueberschreiben: an })}>
        Vorhandene Feldwerte überschreiben
      </Haken>
    </div>
  );
}

// --- Filter und Verzögerung -----------------------------------------------------------------------------------------

function FilterForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  return (
    <div className="wf-form">
      <p className="leise wf-form__text">Trifft eine Bedingung nicht zu, endet der Lauf an dieser Stelle. Die weiteren Schritte laufen dann nicht.</p>
      <Bedingungen bedingungen={(c.bedingungen as Bedingung[]) ?? []} onChange={(b) => onChange({ ...c, bedingungen: b })} leerText="Noch keine Bedingung." />
    </div>
  );
}

type Einheit = "minuten" | "stunden" | "tage";

function VerzoegerungForm({ schritt, onChange }: Props) {
  const c = schritt.config;
  const einheit: Einheit = Number(c.tage) > 0 ? "tage" : Number(c.stunden) > 0 ? "stunden" : Number(c.minuten) > 0 ? "minuten" : "tage";
  const menge = Number(c[einheit]) || 0;
  const setzen = (e: Einheit, n: number) => onChange({ ...c, minuten: 0, stunden: 0, tage: 0, [e]: Math.max(0, n) });
  return (
    <div className="wf-form">
      <div className="wf-form__zeile">
        <Feld label="Warten">
          <span className="wf-inline">
            <input className="eingabe eingabe--schmal" type="number" min={0} value={menge} onChange={(e) => setzen(einheit, Number(e.target.value) || 0)} aria-label="Wartezeit" />
            <Auswahl
              optionen={[
                { wert: "minuten", label: "Minuten" },
                { wert: "stunden", label: "Stunden" },
                { wert: "tage", label: "Tage" },
              ]}
              wert={einheit}
              onWahl={(w) => setzen(w as Einheit, menge)}
              breite={140}
            />
          </span>
        </Feld>
        <Feld label="Dann um (optional)">
          <input className="eingabe eingabe--uhrzeit" type="time" value={String(c.uhrzeit ?? "")} onChange={(e) => onChange({ ...c, uhrzeit: e.target.value || null })} aria-label="Uhrzeit" />
        </Feld>
      </div>
      <Haken an={!!c.nur_werktage} onChange={(an) => onChange({ ...c, nur_werktage: an })}>
        Nur Montag bis Freitag (ein Wochenende rutscht auf Montag)
      </Haken>
      <p className="leise wf-form__text">Zwischen 1 Minute und 365 Tagen. Zeiten gelten in deutscher Ortszeit.</p>
    </div>
  );
}

export function SchrittForm({ schritt, onChange }: Props) {
  switch (schritt.art) {
    case "aufgabe":
    case "anrufaufgabe":
      return <AufgabeForm schritt={schritt} onChange={onChange} />;
    case "email_entwurf":
      return <EmailForm schritt={schritt} onChange={onChange} />;
    case "sms":
      return <SmsForm schritt={schritt} onChange={onChange} />;
    case "zuweisen":
      return <ZuweisenForm schritt={schritt} onChange={onChange} />;
    case "lead_aendern":
      return <LeadAendernForm schritt={schritt} onChange={onChange} />;
    case "filter":
      return <FilterForm schritt={schritt} onChange={onChange} />;
    case "verzoegerung":
      return <VerzoegerungForm schritt={schritt} onChange={onChange} />;
  }
}

/** Kurzer Satz zum Schritt für die zugeklappte Zeile. */
export function schrittZusammenfassung(s: Schritt, nutzerName: (id: string | null | undefined) => string, statusLabel: (id: string | null | undefined) => string | undefined, feldName: (id: string) => string | undefined): string {
  const c = s.config;
  const t = (v: unknown) => String(v ?? "").trim();
  switch (s.art) {
    case "aufgabe":
    case "anrufaufgabe": {
      const tage = Number(c.tage) || 0;
      const faellig = tage === 0 ? "heute" : tage === 1 ? "morgen" : `in ${tage} Tagen`;
      return `${t(c.text) || "(ohne Text)"} · ${faellig}${t(c.uhrzeit) ? `, ${t(c.uhrzeit)} Uhr` : ""}${c.pflicht ? " · Pflicht" : ""}`;
    }
    case "email_entwurf":
      return c.vorlage_id ? "Entwurf aus Vorlage" : t(c.betreff) ? `Entwurf „${t(c.betreff)}“` : "Betreff und Text fehlen";
    case "sms":
      return c.vorlage_id ? "SMS-Entwurf aus Vorlage" : t(c.text) ? `SMS-Entwurf: ${t(c.text).slice(0, 70)}` : "Text fehlt";
    case "zuweisen":
      return c.modus === "leeren" ? "Bearbeiter entfernen" : c.modus === "zufall" ? `Zufällig aus ${((c.nutzer_ids as string[]) ?? []).length} Nutzern` : c.modus === "ausfuehrender" ? "Dem Workflow-Nutzer" : t(c.nutzer_id) ? `An ${nutzerName(t(c.nutzer_id))}` : "Kein Nutzer gewählt";
    case "lead_aendern": {
      const teile: string[] = [];
      if (t(c.status_id)) teile.push(`Status „${statusLabel(t(c.status_id)) ?? "?"}“`);
      for (const id of Object.keys((c.felder as object) ?? {})) teile.push(feldName(id) ?? id);
      return teile.length ? teile.join(", ") : "Nichts gewählt";
    }
    case "filter": {
      const n = ((c.bedingungen as unknown[]) ?? []).length;
      return n ? `${n} ${n === 1 ? "Bedingung" : "Bedingungen"}` : "Keine Bedingung";
    }
    case "verzoegerung": {
      const teile: string[] = [];
      if (Number(c.tage)) teile.push(`${c.tage} ${Number(c.tage) === 1 ? "Tag" : "Tage"}`);
      if (Number(c.stunden)) teile.push(`${c.stunden} Std.`);
      if (Number(c.minuten)) teile.push(`${c.minuten} Min.`);
      return `Warten: ${teile.join(" ") || "0"}${t(c.uhrzeit) ? ` bis ${t(c.uhrzeit)} Uhr` : ""}${c.nur_werktage ? ", nur Werktage" : ""}`;
    }
  }
}
